import React, { useMemo, useCallback, ReactElement } from 'react';
import Table from '../../../../components/table/table.component';
import TableBody from '../../../../components/table-body/table-body.component';
import TableRow from '../../../../components/table-row/table-row.component';
import TableCell from '../../../../components/table-cell/table-cell.component';
import RequiredMark from '../../../../components/required-mark/required-mark.component';
import ConnectorLinkBlock from '../../../../components/connector-link-block/connector-link-block.component';
import FormFieldSelect from '../../../../components/form-field-select/form-field-select.component';
import FormFieldText from '../../../../components/form-field-text/form-field-text.component';
import FormFieldNumber from '../../../../components/form-field-number/form-field-number.component';
import FormFieldCheckbox from '../../../../components/form-field-checkbox/form-field-checkbox.component';
import MenuItemList from '../../../../components/menu-item-list/menu-item-list.component';
import SinglePageTab from '../../../app/common/single-page-tab/single-page-tab.component';
import FormatService from '../../../../services/format-service';
import useShiftState from '../../../../hooks/use-shift-state';
import useScreenMobile from '../../../../hooks/use-screen-mobile';
import { MARKER_COLOR_RED } from '../../../../constants/entity/marker-colors';

const SettingsPortfolioSummary: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isSaving,
    isSaveDisabled,
    isEditMode,
    isRightDrawerOpen,
    portfolioData,
    portfolioGroupList,
    portfolioMarkerColorList,
    formError,
    onCancelForm,
    onSubmitForm,
    onDealConnectEmailClick,
  } = props;

  const isShiftPressed: boolean = useShiftState();
  const isMobileScreen: boolean = useScreenMobile();

  const portfolioMarkerColorDefault: PortfolioMarkerColor | null = portfolioMarkerColorList
  && portfolioMarkerColorList.length ? portfolioMarkerColorList.find((portfolioItem: Portfolio): boolean => (
    portfolioItem.name === MARKER_COLOR_RED
  )) : null;

  const menuItemPortfolioMarkerColorList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: portfolioMarkerColorList,
  }), [portfolioMarkerColorList]);

  const menuItemPortfolioGroupList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemLabelKey: 'name',
    optionList: portfolioGroupList,
  }), [portfolioGroupList]);

  const dealConnectEmailClick: Function = useCallback((): void => {
    if (onDealConnectEmailClick) {
      onDealConnectEmailClick();
    }
  }, []);

  return (
    <SinglePageTab
      isSaving={isSaving}
      isSaveDisabled={isSaveDisabled}
      isEditMode={isEditMode}
      isRightDrawerOpen={isRightDrawerOpen}
      formError={formError}
      onCancelForm={onCancelForm}
      onSubmitForm={onSubmitForm}
    >
      <Table>
        <TableBody isPlain withNoBorders withTwoColumns withHeaderColumn>
          <TableRow>
            <TableCell>
              Name
              {
                isEditMode && (
                  <RequiredMark />
                )
              }
            </TableCell>
            <TableCell>
              {
                isEditMode ? (
                  <FormFieldText isBordered name="name" />
                ) : portfolioData.name
              }
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              Marker Color
            </TableCell>
            <TableCell>
              {
                isEditMode ? (
                  <FormFieldSelect
                    isBordered
                    isReadOnly={isShiftPressed}
                    name="markerColor"
                    optionsTotal={portfolioMarkerColorList && portfolioMarkerColorList.length}
                  >
                    {menuItemPortfolioMarkerColorList}
                  </FormFieldSelect>
                ) : ((portfolioData.markerColor && portfolioData.markerColor.title)
                  || (portfolioMarkerColorDefault && portfolioMarkerColorDefault.title))
              }
            </TableCell>
          </TableRow>
          {
            (isEditMode || (!isEditMode && portfolioData.capitalAllocated)) && (
              <TableRow>
                <TableCell>
                  Capital Allocated
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldNumber isBordered name="capitalAllocated" />
                    ) : portfolioData.capitalAllocated
                  }
                </TableCell>
              </TableRow>
            )
          }
          <TableRow>
            <TableCell>
              Group
            </TableCell>
            <TableCell>
              {
                isEditMode ? (
                  <FormFieldSelect
                    isBordered
                    name="groupId"
                    optionsTotal={portfolioGroupList && portfolioGroupList.length}
                  >
                    {menuItemPortfolioGroupList}
                  </FormFieldSelect>
                ) : (portfolioData.group && portfolioData.group.name)
              }
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              Is Hidden
            </TableCell>
            <TableCell>
              {
                isEditMode ? (
                  <FormFieldCheckbox name="isHidden" />
                ) : FormatService.formatBooleanValue(portfolioData.isHidden)
              }
            </TableCell>
          </TableRow>
          {
            (isEditMode || (!isEditMode && portfolioData.dealConnectorEmail)) && (
              <TableRow>
                <TableCell>
                  Deal Connector Email
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldText isBordered name="dealConnectorEmail" />
                    ) : (
                      !isEditMode && portfolioData.dealConnectorEmail && (
                        <ConnectorLinkBlock
                          isEmail
                          isColumnView={isMobileScreen}
                          isConnected={portfolioData.isDealConnectorEnabled}
                          link={portfolioData.dealConnectorEmail}
                          onConnectClick={dealConnectEmailClick}
                        />
                      )
                    )
                  }
                </TableCell>
              </TableRow>
            )
          }
        </TableBody>
      </Table>
    </SinglePageTab>
  );
});

export default SettingsPortfolioSummary;
